/**
 * useSessionKeepalive — periodic session validation ping.
 *
 * The server session TTL is 8 hours with sliding window expiry.
 * The sliding window only extends when validateSession() is called.
 * If the user has the app open but doesn't make API calls for hours,
 * their session could expire.
 *
 * This hook sends a lightweight /auth/validate every 30 minutes
 * to extend the session sliding window.
 */

import { useEffect, useRef } from "react";
import { apiFetch } from "../utils/apiClient";

/** Ping interval: 30 minutes */
const KEEPALIVE_INTERVAL = 30 * 60 * 1000;

export function useSessionKeepalive(isLoggedIn: boolean) {
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!isLoggedIn) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const ping = () => {
      apiFetch("/auth/validate", {
        method: "POST",
        retry: { noRetry: true },
      }).catch(() => {
        // Silent — if it fails, the auth gate will handle it on next API call
      });
    };

    // Don't ping immediately — wait for first interval
    intervalRef.current = setInterval(ping, KEEPALIVE_INTERVAL);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isLoggedIn]);
}
